/**
 * useMetricPaint
 *
 * Lets the Monitoring page tint an existing feature-collection group with a
 * metric colormap instead of adding a second source for the same polygons.
 * The metric raster is then slotted between that group's fill and line layers,
 * so nothing paints over the data.
 *
 * Paint is handed out as expressions and consumed through MapLayer's `options`
 * prop, not applied with setPaintProperty — MapLayer rebuilds its options on
 * every style.load re-add, which would silently revert an external mutation.
 */
import { computed, ref, unref } from 'vue'
import { CHOROPLETH_DEFS, rampColor } from '@/utils/monitoring/colormaps'

const DEFAULT_COLOR = ['coalesce', ['get', 'color'], '#0059ff']
// Polygons read as outlines. The fill layer stays (it is the click target and
// the anchor overlays insert against) but paints nothing on its own.
const DEFAULT_FILL_OPACITY = 0
const PENDING_COLOR = '#d6d5d0'

const overlay = ref(null)

/**
 * @param {{groupId: string, metric: string, values: Record<string, number>, pending: string[]}|null} next
 */
export function setMetricOverlay(next) {
  overlay.value = next || null
}

export function clearMetricOverlay() {
  overlay.value = null
}

function buildPaint(active) {
  const def = CHOROPLETH_DEFS[active.metric]
  if (!def) return null

  const colorCases = []
  for (const [code, value] of Object.entries(active.values || {})) {
    const color = rampColor(def, Number(value))
    if (color) colorCases.push(code, color)
  }
  const pending = (active.pending || []).filter((code) => !(code in (active.values || {})))
  if (!colorCases.length && !pending.length) return null

  const valued = colorCases.filter((_, i) => i % 2 === 0)
  // Mapbox rejects an empty label array, so each group is only emitted when populated.
  const colorExpr = ['match', ['get', 'code'], ...colorCases]
  const opacityExpr = ['match', ['get', 'code']]
  if (valued.length) opacityExpr.push(valued, 0.65)
  if (pending.length) {
    colorExpr.push(pending, PENDING_COLOR)
    opacityExpr.push(pending, 0.35)
  }
  colorExpr.push(DEFAULT_COLOR)
  opacityExpr.push(DEFAULT_FILL_OPACITY)

  return { fillColor: colorExpr, lineColor: colorExpr, fillOpacity: opacityExpr }
}

/**
 * @param {import('vue').Ref<string>|string} groupId
 * @returns {import('vue').ComputedRef<{fillColor: any, lineColor: any, fillOpacity: any}|null>}
 */
export function useMetricPaint(groupId) {
  return computed(() => {
    const active = overlay.value
    if (!active || active.groupId !== unref(groupId)) return null
    return buildPaint(active)
  })
}

export { DEFAULT_COLOR, DEFAULT_FILL_OPACITY }
